import * as React from 'react';
import { Button, Col, Row, DatePicker, Input, Select } from 'antd';
import moment from 'moment';
import ReportChartCard from './ReportChartCard';
import { NATIVE_REPORT_LAYOUTS } from './reporting.layouts';
import { buildRequestFilters } from './reporting.requestFilters';

interface ReportPageProps {
  reportKey: string;
  onBack?: (open: boolean) => void;
  // Fixed filters merged into every chart request, e.g. { planId } on the RFQ plan detail page.
  extraFilters?: { [key: string]: string };
}

interface DraftFilters {
  fromDate?: moment.Moment;
  toDate?: moment.Moment;
  // Keyed by ReportTextFilter.key — which text filters exist depends on the report.
  text: { [key: string]: string };
}

type Unit = 'days' | 'weeks' | 'months' | 'years';
const RANGE_OPTIONS: { key: string; label: string; amount: number; unit: Unit }[] = [
  { key: '1d', label: '1 Day', amount: 1, unit: 'days' },
  { key: '1w', label: '1 Week', amount: 1, unit: 'weeks' },
  { key: '1m', label: '1 Month', amount: 1, unit: 'months' },
  { key: '3m', label: '3 Months', amount: 3, unit: 'months' },
  { key: '6m', label: '6 Months', amount: 6, unit: 'months' },
  { key: '1y', label: '1 Year', amount: 1, unit: 'years' },
];
const DEFAULT_RANGE = '1w';
const CUSTOM = 'custom';

const presetDates = (key: string): { fromDate: moment.Moment; toDate: moment.Moment } => {
  const opt = RANGE_OPTIONS.find((o) => o.key === key) || RANGE_OPTIONS[1];
  return { fromDate: moment().subtract(opt.amount, opt.unit), toDate: moment() };
};

const buildApplied = (draft: DraftFilters): { [key: string]: string } => {
  const next: { [key: string]: string } = {};
  if (draft.fromDate) {
    next.fromDate = draft.fromDate.format('YYYY-MM-DD');
  }
  if (draft.toDate) {
    next.toDate = draft.toDate.format('YYYY-MM-DD');
  }
  Object.keys(draft.text).forEach((key) => {
    const value = (draft.text[key] || '').trim();
    if (value) {
      next[key] = value;
    }
  });
  return next;
};

const initialDraft = (rangeKey: string): DraftFilters => ({
  ...presetDates(rangeKey),
  text: {},
});

const ReportPage = ({ reportKey, onBack, extraFilters }: ReportPageProps) => {
  const layout = NATIVE_REPORT_LAYOUTS[reportKey];
  const defaultRange = (layout && layout.defaultRange) || DEFAULT_RANGE;
  const [preset, setPreset] = React.useState<string>(defaultRange);
  const [draft, setDraft] = React.useState<DraftFilters>(() => initialDraft(defaultRange));
  // Open on the report's default window (1 week unless the layout says otherwise) so the first load is fast.
  const [applied, setApplied] = React.useState<object>(() => buildApplied(initialDraft(defaultRange)));

  if (!layout) {
    return <div style={{ margin: '15px 20px' }}>This report is not available.</div>;
  }

  const onPreset = (key: string) => {
    setPreset(key);
    // Custom hands the date pickers over to the user: keep whatever window is on
    // screen as the starting point and wait for Apply. A preset owns its own
    // dates, so it fills them in and applies straight away.
    if (key === CUSTOM) {
      return;
    }
    const next: DraftFilters = { ...draft, ...presetDates(key) };
    setDraft(next);
    setApplied(buildApplied(next));
  };

  const apply = () => {
    setApplied(buildApplied(draft));
  };

  const clear = () => {
    setPreset(defaultRange);
    const next = initialDraft(defaultRange);
    setDraft(next);
    setApplied(buildApplied(next));
  };

  const isCustom = preset === CUSTOM;
  // A plan-scoped report is all-time within the plan, so the date controls do not apply.
  const planMode = Boolean(extraFilters && extraFilters.planId);
  const showFilterBar = !planMode || layout.textFilters.length > 0;
  const inputStyle: React.CSSProperties = { marginRight: 8, marginBottom: 8 };

  return (
    <div style={{ margin: '0 20px 15px' }}>
      {onBack ? (
        <Button icon="arrow-left" onClick={() => onBack(false)}>
          Back
        </Button>
      ) : null}
      <h2 style={{ marginTop: 0, marginBottom: 12 }}>{layout.title}</h2>

      {showFilterBar && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
          {!planMode && (
            <>
              <Select style={{ width: 130, ...inputStyle }} value={preset} onChange={onPreset}>
                {RANGE_OPTIONS.map((o) => (
                  <Select.Option key={o.key} value={o.key}>
                    {o.label}
                  </Select.Option>
                ))}
                <Select.Option key={CUSTOM} value={CUSTOM}>
                  Custom
                </Select.Option>
              </Select>
              <DatePicker
                style={inputStyle}
                placeholder="From Date"
                disabled={!isCustom}
                value={draft.fromDate}
                onChange={(d) => setDraft({ ...draft, fromDate: d || undefined })}
              />
              <DatePicker
                style={inputStyle}
                placeholder="To Date"
                disabled={!isCustom}
                value={draft.toDate}
                onChange={(d) => setDraft({ ...draft, toDate: d || undefined })}
              />
            </>
          )}
          {layout.textFilters.map((f) => (
            <Input
              key={f.key}
              style={{ width: 170, ...inputStyle }}
              placeholder={f.placeholder}
              value={draft.text[f.key] || ''}
              onChange={(e) => setDraft({ ...draft, text: { ...draft.text, [f.key]: e.target.value } })}
              onPressEnter={apply}
            />
          ))}
          <Button type="primary" style={inputStyle} onClick={apply}>
            Apply
          </Button>
          <Button style={{ marginBottom: 8 }} onClick={clear}>
            Clear
          </Button>
        </div>
      )}

      {/* flex so mixed-width cards (numbers, bars, tables) wrap row by row instead of floating into gaps */}
      <Row type="flex" gutter={16}>
        {layout.charts.map((c) => (
          <Col key={c.key} span={c.width * 2}>
            <ReportChartCard reportKey={reportKey} reportTitle={layout.title} chart={c} filters={buildRequestFilters(applied as { [key: string]: string }, extraFilters)} />
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default ReportPage;
